import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../../src/lib/supabase';
import { buscarTudoPorChave } from '../../../../src/lib/paginacaoKeyset';
import { normalizarCpfCnpj } from '../../../../src/lib/cobrancaCliente';
import { chaveCliente, derivarStatusBoleto, valorDevido, STATUS_FATURA_COM_BOLETO } from '../../../../src/lib/statusBoleto';
import { normalizeUcCodigo, ucCodigosDaFatura } from '../../../../src/lib/ucCodigo';
import { CadastroUc, ClienteConsolidado, FaturaInadimplente, FiltrosGlobais, UcInadimplente, initialFiltros } from '../types';
import { enderecoUcTexto, telefonesUc, temCoordenadaConfiavel } from '../../../../src/lib/enderecoUc';
import { classifyRisk, classifyScore, isInterno, normalizeCompetencia, diasDesdeCompetencia } from '../utils';

import { TIPOS_AVISO, recebeuTodosOsAvisos, SUFIXO_CONTATO_HUMANO } from '../lib/reguaAvisos';
import { situacaoEfetivaUc, classificarRateioCliente, ordemMesReferencia } from '../lib/situacaoRateio';

// A régua de avisos mora em lib/reguaAvisos.ts (a exportação em Excel usa a
// mesma regra). Reexportado aqui porque as telas já importavam daqui.
export { recebeuTodosOsAvisos };

export function useInadimplenciaData() {
  const [rawFaturas, setRawFaturas] = useState<FaturaInadimplente[]>([]);
  const [cadastroPorUc, setCadastroPorUc] = useState<Map<string, CadastroUc>>(new Map());
  const [avisosPorFatura, setAvisosPorFatura] = useState<Map<string, Set<string>>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<FiltrosGlobais>(initialFiltros);
  const [dataExtraction, setDataExtraction] = useState('');

  /**
   * Cadastro das UCs indexado pelo número normalizado.
   *
   * Serve a duas coisas que a fatura sozinha não entrega:
   *
   * 1. **CPF/CNPJ.** As 230 faturas vencidas há mais de 180 dias têm
   *    `cliente_cpf_cnpj` VAZIO (medido em 25/08/2026) — são de base migrada,
   *    anterior ao robô que passou a gravar o documento. Sem resolver pelo
   *    cadastro da UC, `ClienteConsolidado.cpfCnpj` fica nulo e o botão
   *    "Gerenciar Cobrança" abre dizendo "cadastre o documento na Unidade
   *    Consumidora" — sendo que o documento ESTÁ lá: a UC resolve 223 das 230.
   *    Pior: 80 desses clientes já têm linha em `cobranca_clientes`, com
   *    responsável, status e histórico — tudo invisível porque a chave usada
   *    para achar a linha é justamente o campo vazio.
   *
   * 2. **Situação do cadastro**, que é o "rateio finalizado" do chamado.
   *
   * São ~1.6k UCs: duas páginas, uma vez por carregamento da Central.
   */
  const fetchCadastroUcs = async () => {
    const map = new Map<string, CadastroUc>();
    const data = await buscarTudoPorChave<any>(
      (apos, tamanho) => {
        let q = supabase
          .from('unidades_consumidoras')
          // Endereço, contato e coordenada entram aqui porque esta consulta já
          // varre a tabela inteira: acrescentar coluna custa zero requisição, e
          // sem elas a rota de cobrança teria que reler as mesmas 1.8k UCs.
          .select('id, numero_uc, outro_numero_uc, cpf_cnpj_cliente, situacao, titular_fatura, logradouro, numero, complemento, bairro, cidade, estado, cep, endereco, latitude, longitude, geocode_status, geocode_label, whatsapp_cliente, whatsapp_cliente_2')
          .order('id', { ascending: true })
          .limit(tamanho);
        if (apos != null) q = q.gt('id', apos);
        return q as any;
      },
      (u) => u.id,
    );
    data.forEach((u: any) => {
        const info: CadastroUc = {
          id: u.id ?? null,
          cpf: normalizarCpfCnpj(u.cpf_cnpj_cliente),
          situacao: u.situacao ?? null,
          titular_fatura: u.titular_fatura ?? null,
          logradouro: u.logradouro ?? null,
          numero: u.numero ?? null,
          complemento: u.complemento ?? null,
          bairro: u.bairro ?? null,
          cidade: u.cidade ?? null,
          estado: u.estado ?? null,
          cep: u.cep ?? null,
          endereco: u.endereco ?? null,
          latitude: u.latitude ?? null,
          longitude: u.longitude ?? null,
          geocode_status: u.geocode_status ?? null,
          geocode_label: u.geocode_label ?? null,
          whatsapp_cliente: u.whatsapp_cliente ?? null,
          whatsapp_cliente_2: u.whatsapp_cliente_2 ?? null,
        };
        // As duas numerações da Equatorial apontam para o mesmo cadastro; a
        // fatura pode vir com qualquer uma das duas.
      [u.numero_uc, u.outro_numero_uc].forEach(n => {
        const chave = normalizeUcCodigo(n);
        if (chave && !map.has(chave)) map.set(chave, info);
      });
    });
    return map;
  };

  /**
   * Status de cada número de UC na planilha de rateio mais recente em que ele
   * aparece, e as UCs com cancelamento concluído (#6166). Ver lib/situacaoRateio.ts.
   * Falha aqui não derruba a tela: sem isso, vale só o cadastro, como antes.
   */
  const fetchFontesRateio = async () => {
    const planilha = new Map<string, { ordem: number; status: string }>();
    const canceladas = new Set<string>();
    try {
      const [{ data: rateios }, itens, { data: sols }] = await Promise.all([
        supabase.from('rateios_credito').select('id, mes_referencia'),
        buscarTudoPorChave<any>(
          (apos, tamanho) => {
            let q = supabase
              .from('rateios_credito_itens')
              .select('id, rateio_id, uc, outro_numero_uc, status')
              .order('id', { ascending: true })
              .limit(tamanho);
            if (apos != null) q = q.gt('id', apos);
            return q as any;
          },
          (i) => i.id,
        ),
        supabase.from('solicitacoes_cancelamento').select('uc_id, uc_numero').eq('status', 'concluida'),
      ]);
      const ordemDoRateio = new Map<string, number>((rateios || []).map((r: any) => [r.id, ordemMesReferencia(r.mes_referencia)]));
      itens.forEach((i: any) => {
        const ordem = ordemDoRateio.get(i.rateio_id) ?? -1;
        if (!i.status) return;
        [i.uc, i.outro_numero_uc].forEach(n => {
          const chave = normalizeUcCodigo(n);
          if (!chave) return;
          const atual = planilha.get(chave);
          if (!atual || ordem > atual.ordem) planilha.set(chave, { ordem, status: i.status });
        });
      });
      (sols || []).forEach((s: any) => {
        if (s.uc_id != null) canceladas.add(`id:${s.uc_id}`);
        const chave = normalizeUcCodigo(s.uc_numero);
        if (chave) canceladas.add(chave);
      });
    } catch (e) {
      console.warn('Situação pelo rateio indisponível; usando só o cadastro:', e);
    }
    return { planilha, canceladas };
  };

  const fetchAvisosPorFatura = async (faturaIds: string[]) => {
    const map = new Map<string, Set<string>>();
    const chunkSize = 300;
    for (let i = 0; i < faturaIds.length; i += chunkSize) {
      const chunk = faturaIds.slice(i, i + chunkSize);
      if (chunk.length === 0) continue;
      const { data, error: err } = await supabase
        .from('lembrete_cobranca_log')
        .select('fatura_id, tipo_lembrete, status')
        .in('fatura_id', chunk)
        .in('status', ['enviado', 'reenviado', 'suprimido']);

      if (err) throw err;
      (data || []).forEach((row: any) => {
        const key = String(row.fatura_id);
        if (!map.has(key)) map.set(key, new Set());
        // Suprimido = o aviso ficou para o contato humano (ver reguaAvisos.ts).
        map.get(key)!.add(row.status === 'suprimido' ? `${row.tipo_lembrete}${SUFIXO_CONTATO_HUMANO}` : row.tipo_lembrete);
      });
    }
    return map;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Antes esta consulta filtrava `status_boleto_vigor = 'Vencido'` no
      // servidor. O campo cru está dessincronizado nos dois sentidos (ver
      // src/lib/statusBoleto.ts), então isso trazia 71 boletos já cancelados
      // como inadimplência e deixava de fora vencidos cujo status ainda não
      // tinha sido corrigido. Agora buscamos o universo com boleto e aplicamos
      // a MESMA derivação do Painel Financeiro Executivo — é o que faz os dois
      // painéis fecharem no mesmo número (chamado #3789).
      // As faturas e o cadastro de UC não dependem um do outro: saem juntos, em
      // vez de a segunda consulta só começar quando a primeira termina.
      const [candidatas, cadastroUcs, fontesRateio] = await Promise.all([
        buscarTudoPorChave<any>(
          (apos, tamanho) => {
            let q = supabase
              .from('faturas')
              .select('id, cliente_nome, cliente_cpf_cnpj, uc_codigo, valor_boleto_vigor, vencimento_boleto, data_vencimento, status_boleto_vigor, valor_parcelamentos_boleto, parcelamento_descricao, saldo_acumulado_kwh, mes_referencia, status_fatura, motivo_cancelamento_boleto, end_cidade, data_pgto_boleto, quitado_em, cancelado_em, valor_emissao_boleto_unico, end_logradouro, end_numero, end_bairro, end_cep, end_uf, pdf_fatura, url_boleto, url_boleto_combinado, pdf_boleto_combinado, token_boleto_publico, linha_digitavel_boleto, codigo_pix_boleto, num_cobranca_boleto, dono_anterior_troca_id, cobranca_agrupada')
              .eq('ambiente', 'producao')
              // Cliente próprio da parceira (boleto avulso da Gran): a Vigor não recebe nada dele.
              .eq('fora_parceria_vigor', false)
              .in('status_fatura', STATUS_FATURA_COM_BOLETO as unknown as string[])
              .order('id', { ascending: true })
              .limit(tamanho);
            if (apos != null) q = q.gt('id', apos);
            return q as any;
          },
          (f) => f.id,
        ),
        fetchCadastroUcs(),
        fetchFontesRateio(),
      ]);

      const agora = new Date();
      const vencidas = candidatas
        .filter(f => derivarStatusBoleto(f, agora) === 'Vencido')
        .map(f => {
          // Fatura do dono anterior (troca de dono da UC): o cadastro da UC hoje é
          // de quem comprou o estabelecimento — CPF e situação dele não valem aqui.
          const codigos = ucCodigosDaFatura(f.uc_codigo);
          const info = f.dono_anterior_troca_id
            ? undefined
            : codigos
                .map(c => cadastroUcs.get(c))
                .find(Boolean);
          // Situação efetiva (#6166): cadastro + planilha do rateio + cancelamento concluído.
          const planilha = codigos.map(c => fontesRateio.planilha.get(c)).filter(Boolean)
            .sort((a, b) => b!.ordem - a!.ordem)[0]?.status ?? null;
          const cancelada = codigos.some(c => fontesRateio.canceladas.has(c))
            || (info?.id != null && fontesRateio.canceladas.has(`id:${info.id}`));
          const situacaoUc = f.dono_anterior_troca_id
            ? null
            : situacaoEfetivaUc(info?.situacao, planilha, cancelada);
          return {
            ...f,
            // O documento da fatura tem prioridade quando existe; o cadastro da
            // UC entra como resgate — mesma precedência já usada em
            // resolveCpfCnpjCliente para o caminho inverso.
            cliente_cpf_cnpj: normalizarCpfCnpj(f.cliente_cpf_cnpj) || info?.cpf || null,
            situacao_uc: situacaoUc,
          };
        });

      setRawFaturas(vencidas as FaturaInadimplente[]);
      setCadastroPorUc(cadastroUcs);
      // Só as vencidas precisam do log de lembretes — buscar sobre o universo
      // inteiro multiplicaria por 6 o número de requisições.
      setAvisosPorFatura(await fetchAvisosPorFatura(vencidas.map(f => String(f.id))));
      setDataExtraction(agora.toLocaleString('pt-BR'));
    } catch (err: any) {
      console.error('Error fetching inadimplência:', err);
      setError(err.message || 'Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const {
    clientesCompletos,
    clientesFiltrados,
    cidadesUnicas,
    totalAberto,
    totalAbertoFiltrado
  } = useMemo(() => {
    if (!rawFaturas.length) return { clientesCompletos: [], clientesFiltrados: [], cidadesUnicas: [], totalAberto: 0, totalAbertoFiltrado: 0 };

    // Recorte de período ANTES da consolidação: valores, risco, score e curva
    // ABC passam a refletir apenas as faturas do período. Dois modos: por
    // vencimento (chave yyyy-mm-dd) ou por competência (chave yyyy-mm).
    const { dataInicio, dataFim, periodoModo } = filtros;
    const faturasBase = (dataInicio || dataFim)
      ? rawFaturas.filter(f => {
          const chave = periodoModo === 'competencia'
            ? normalizeCompetencia(f.mes_referencia)
            : (f.vencimento_boleto || f.data_vencimento);
          if (!chave) return false;
          if (dataInicio && chave < dataInicio) return false;
          if (dataFim && chave > dataFim) return false;
          return true;
        })
      : rawFaturas;

    const map = new Map<string, ClienteConsolidado>();

    faturasBase.forEach(f => {
      const nome = chaveCliente(f.cliente_nome);
      if (!map.has(nome)) {
        map.set(nome, {
          nome, cpfCnpj: null, valorTotal: 0, qtdFaturas: 0, saldoAcumulado: 0,
          ucs: [], competencias: [], cidades: [], vencMaisAntigo: null,
          pct: 0, pctAcum: 0, curvaAbc: 'C', risco: 'RISCO BAIXO', score: 'A', interno: isInterno(nome),
          situacoesUc: [], rateioFinalizado: false, situacaoRateio: 'nao_informada', diasAtrasoMax: 0,
          diasDividaMax: 0, competenciaMaisAntiga: null,
          faturas: []
        });
      }
      const c = map.get(nome)!;
      if (!c.cpfCnpj) {
        const cpfCnpj = normalizarCpfCnpj(f.cliente_cpf_cnpj);
        if (cpfCnpj) c.cpfCnpj = cpfCnpj;
      }
      // valorDevido, não valor_boleto_vigor: em boleto único/parceira esse campo
      // é 0 e o valor cobrado está em valor_emissao_boleto_unico.
      c.valorTotal += valorDevido(f);
      c.qtdFaturas += 1;
      c.saldoAcumulado += (f.saldo_acumulado_kwh || 0);
      if (f.uc_codigo && !c.ucs.includes(f.uc_codigo)) c.ucs.push(f.uc_codigo);
      if (f.mes_referencia && !c.competencias.includes(f.mes_referencia)) c.competencias.push(f.mes_referencia);
      const cid = f.end_cidade ? f.end_cidade.trim().toUpperCase() : 'NÃO INFORMADA';
      if (!c.cidades.includes(cid)) c.cidades.push(cid);
      const venc = f.vencimento_boleto || f.data_vencimento;
      if (venc && (!c.vencMaisAntigo || venc < c.vencMaisAntigo)) c.vencMaisAntigo = venc;
      if (f.situacao_uc && !c.situacoesUc.includes(f.situacao_uc)) c.situacoesUc.push(f.situacao_uc);
      const comp = normalizeCompetencia(f.mes_referencia);
      if (comp && (!c.competenciaMaisAntiga || comp < c.competenciaMaisAntiga)) c.competenciaMaisAntiga = comp;
      c.faturas.push(f);
    });

    // Derivados que dependem do cliente inteiro, não de uma fatura.
    const hoje = new Date();
    const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime();
    map.forEach(c => {
      // Só chamamos de "rateio finalizado" quando TODAS as UCs conhecidas do
      // cliente estão canceladas. Cliente com uma UC ativa e outra cancelada
      // continua ativo — a dívida antiga dele se resolve na relação corrente,
      // não na régua de encerrado.
      c.situacaoRateio = classificarRateioCliente(c.situacoesUc);
      c.rateioFinalizado = c.situacaoRateio === 'encerrado';
      if (c.vencMaisAntigo) {
        const [ano, mes, dia] = c.vencMaisAntigo.split('T')[0].split('-').map(Number);
        const venc = new Date(ano, (mes || 1) - 1, dia || 1).getTime();
        c.diasAtrasoMax = Math.max(0, Math.floor((hojeSemHora - venc) / 86400000));
      }
      // A idade da dívida é a MAIOR das duas medidas. Só o vencimento
      // subestima a fatura migrada do Power Rev, cujo boleto foi reemitido
      // com data nova; só a competência subestima a avulsa de parcelamento,
      // que tem competência do mês corrente e vencimento já vencido.
      const idadeCompetencia = c.faturas.reduce((maior, f) => {
        const d = diasDesdeCompetencia(f.mes_referencia, hoje);
        return d !== null && d > maior ? d : maior;
      }, 0);
      c.diasDividaMax = Math.max(c.diasAtrasoMax, idadeCompetencia);
    });

    let clientes = Array.from(map.values()).sort((a, b) => b.valorTotal - a.valorTotal);
    const totalAberto = clientes.reduce((s, c) => s + c.valorTotal, 0);

    let cum = 0;
    clientes.forEach(c => {
      c.pct = totalAberto > 0 ? (c.valorTotal / totalAberto) * 100 : 0;
      cum += c.pct;
      c.pctAcum = cum;
      c.curvaAbc = cum <= 80 ? 'A' : cum <= 95 ? 'B' : 'C';
      c.risco = classifyRisk(c.valorTotal, c.qtdFaturas, c.saldoAcumulado, c.interno);
      c.score = classifyScore(c.vencMaisAntigo);
    });

    const cidadesUnicas = Array.from(new Set(clientes.flatMap(c => c.cidades))).sort();

    // Filtros
    let filtrados = clientes.filter(c => {
      if (filtros.cidade !== 'Todas' && !c.cidades.includes(filtros.cidade)) return false;
      if (filtros.risco !== 'Todos' && c.risco !== filtros.risco) return false;
      if (filtros.score !== 'Todos' && c.score !== filtros.score) return false;
      if (filtros.busca) {
        const b = filtros.busca.toLowerCase();
        if (!c.nome.toLowerCase().includes(b) && !c.ucs.some(u => u.includes(b))) return false;
      }
      if (filtros.saldoAcumulado !== 'Todos') {
        if (filtros.saldoAcumulado === 'Com Saldo' && c.saldoAcumulado <= 0) return false;
        if (filtros.saldoAcumulado === 'Sem Saldo' && c.saldoAcumulado > 0) return false;
      }
      return true;
    });

    const totalAbertoFiltrado = filtrados.reduce((s, c) => s + c.valorTotal, 0);

    // Recalcular % para os filtrados
    filtrados.forEach(c => {
      c.pct = totalAbertoFiltrado > 0 ? (c.valorTotal / totalAbertoFiltrado) * 100 : 0;
    });

    return { clientesCompletos: clientes, clientesFiltrados: filtrados, cidadesUnicas, totalAberto, totalAbertoFiltrado };
  }, [rawFaturas, filtros]);

  /**
   * As mesmas faturas, agrupadas por UC — a lista de paradas em potencial da
   * rota de cobrança.
   *
   * Deriva de `clientesFiltrados` de propósito: assim a aba de rota respeita os
   * filtros globais (período, risco, score, busca) sem duplicar regra nenhuma.
   *
   * O filtro de cidade, porém, precisa de um refino. Lá em cima ele é aplicado
   * ao CLIENTE — basta uma UC na cidade escolhida para todas as faturas dele
   * entrarem, o que é correto para negociar a dívida inteira. Para montar uma
   * viagem seria errado: escolher "Aparecida de Goiânia" traria junto as UCs
   * desse cliente em Goiânia, e o cobrador veria paradas fora da cidade que
   * planejou rodar.
   */
  const ucsInadimplentes = useMemo<UcInadimplente[]>(() => {
    const porUc = new Map<string, UcInadimplente>();

    clientesFiltrados.forEach(cliente => {
      cliente.faturas.forEach(f => {
        // Dívida do dono anterior da UC (troca de dono) não vira parada: o endereço
        // e o telefone da UC agora são de quem comprou o estabelecimento. Ela segue
        // na lista de clientes, no nome dele, e na régua de WhatsApp/e-mail.
        if (f.dono_anterior_troca_id) return;
        // Uma cobrança avulsa pode trazer os DOIS números da unidade no campo
        // ("10015106754 / 10039677999"). O cadastro é procurado por qualquer um
        // deles — senão a fatura vira uma UC fantasma, uma parada a mais no
        // mesmo portão e um termo de adesão cobrado duas vezes.
        const codigos = ucCodigosDaFatura(f.uc_codigo);
        const chaveFatura = codigos[0];
        if (!chaveFatura) return;
        const cadastro = codigos.map(c => cadastroPorUc.get(c)).find(Boolean) ?? null;

        // A unidade é agrupada pelo CADASTRO, não pelo código impresso na fatura.
        // A mesma unidade física tem dois números — `numero_uc` e
        // `outro_numero_uc` — e o robô grava ora um, ora outro em
        // `faturas.uc_codigo`. Agrupar pelo código faria a UC 453 aparecer duas
        // vezes na lista (como 355307501202 e como 10025066305), somando duas
        // paradas no mesmo portão e contando a dívida dela em dobro no rodapé.
        // Sem cadastro não há como saber que são a mesma: aí o código é o que há.
        const chave = cadastro?.id != null ? `uc:${cadastro.id}` : chaveFatura;

        const cidade = (cadastro?.cidade || f.end_cidade || '').trim().toUpperCase() || 'NÃO INFORMADA';
        if (filtros.cidade !== 'Todas' && cidade !== filtros.cidade) return;

        if (!porUc.has(chave)) {
          porUc.set(chave, {
            chave,
            ucId: cadastro?.id ?? null,
            ucCodigo: f.uc_codigo,
            titular: cadastro?.titular_fatura ?? null,
            clienteNome: cliente.nome,
            cpfCnpj: cliente.cpfCnpj ?? cadastro?.cpf ?? null,
            telefones: telefonesUc(cadastro),
            enderecoTexto: enderecoUcTexto(cadastro, f),
            cidade,
            bairro: cadastro?.bairro ?? f.end_bairro ?? null,
            latitude: cadastro?.latitude ?? null,
            longitude: cadastro?.longitude ?? null,
            geocodeStatus: cadastro?.geocode_status ?? null,
            coordenadaConfiavel: temCoordenadaConfiavel(cadastro),
            situacaoUc: f.situacao_uc ?? cadastro?.situacao ?? null,
            qtdFaturas: 0,
            valorTotal: 0,
            vencMaisAntigo: null,
            diasAtrasoMax: 0,
            pdfFatura: null,
            faturas: [],
            cadastro,
          });
        }

        const uc = porUc.get(chave)!;
        uc.qtdFaturas += 1;
        uc.valorTotal += valorDevido(f);
        uc.faturas.push(f);

        const venc = f.vencimento_boleto || f.data_vencimento;
        if (venc && (!uc.vencMaisAntigo || venc < uc.vencMaisAntigo)) uc.vencMaisAntigo = venc;

        // O endereço do cadastro pode estar incompleto; a fatura costuma ter o
        // número da casa que falta nele. Só recalcula enquanto não houver
        // endereço, para não ficar trocando de texto a cada fatura.
        if (!uc.enderecoTexto) uc.enderecoTexto = enderecoUcTexto(cadastro, f);
      });
    });

    const hoje = new Date();
    const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime();

    const lista = Array.from(porUc.values());
    lista.forEach(uc => {
      if (uc.vencMaisAntigo) {
        const [ano, mes, dia] = uc.vencMaisAntigo.split('T')[0].split('-').map(Number);
        const venc = new Date(ano, (mes || 1) - 1, dia || 1).getTime();
        uc.diasAtrasoMax = Math.max(0, Math.floor((hojeSemHora - venc) / 86400000));
      }
      // O PDF da distribuidora que vai na mensagem é o da fatura MAIS RECENTE
      // que tiver um: é a que o cliente reconhece quando o cobrador chega.
      const comPdf = uc.faturas
        .filter(f => (f.pdf_fatura || '').trim())
        .sort((a, b) => String(b.vencimento_boleto || b.data_vencimento || '')
          .localeCompare(String(a.vencimento_boleto || a.data_vencimento || '')));
      uc.pdfFatura = comPdf.length > 0 ? String(comPdf[0].pdf_fatura) : null;
    });

    return lista.sort((a, b) => b.valorTotal - a.valorTotal);
  }, [clientesFiltrados, cadastroPorUc, filtros.cidade]);

  return {
    rawFaturas,
    avisosPorFatura,
    ucsInadimplentes,
    clientesFiltrados,
    clientesCompletos,
    cidadesUnicas,
    totalAberto,
    totalAbertoFiltrado,
    loading,
    error,
    filtros,
    setFiltros,
    dataExtraction,
    refetch: fetchData
  };
}

export { TIPOS_AVISO };
